import React, { useState, useEffect, ReactNode } from "react";
import { Spinner } from "@chakra-ui/react";
import { Input } from "components/RFQ/Input";
import { Button } from "newui/components/Button";
import {
  Clarification,
  ClarificationType,
  ClarificationImpact,
  ClarificationStatus,
  CreateClarificationPayload,
  UpdateClarificationPayload,
  SubmitClarificationResponsePayload,
} from "../../types/clarification.type";
import {
  CLARIFICATION_IMPACT_LABELS,
} from "../../constants/clarification.constants";
import { Modal } from "newui/components/ui/Modal";
import { FiEdit2, FiFileText, FiMessageCircle, FiPlusCircle } from "react-icons/fi";
import { ClarificationSupplierResponse } from "./components/ClarificationSupplierResponse";
import { FileableFile, UploadFile } from "types/storage.types";
import { Header } from "./components/Header";
import { ClientCreation } from "./components/ClientCreation";
import { ViewSupplierResponse } from "./components/ViewSupplierResponse";
import { usePreventAllNavigationDuringUpload } from "newui/components/FileUpload/hooks/usePreventNavigationDuringUpload";

export type ModalMode = "create" | "view" | "respond" | "edit" | "edit-full";

interface ClarificationModalProps {
  clarification?: Clarification | null;
  clarificationTypes: ClarificationType[];
  isClientView: boolean;
  isOpen: boolean;
  supplierName?: string;
  onClientSave: (
    payload: CreateClarificationPayload | UpdateClarificationPayload,
    clarificationId?: number
  ) => Promise<boolean>;
  onClose: (files?: FileableFile[]) => void;
  onSupplierSave: (
    payload: SubmitClarificationResponsePayload,
    clarificationId?: number
  ) => Promise<boolean>;
}

export const ClarificationModal: React.FC<ClarificationModalProps> = ({
  isOpen,
  onClose,
  clarification,
  clarificationTypes,
  isClientView,
  supplierName,
  onClientSave,
  onSupplierSave,
}) => {
  const { isSafeToClose } = usePreventAllNavigationDuringUpload();
  const [uploadFiles, setUploadFiles] = useState<FileableFile[]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    clarification_text: "",
    clarification_type_id: 0,
    relates_to: "",
    owner: "",
    clarification_impact_id: null as ClarificationImpact,
    status: null as ClarificationStatus,
    final_response: "",
    files: [] as UploadFile[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getMode = (): ModalMode => {
    if (!clarification) {
      return "create";
    }
    if (isClientView) {
      if (clarification.is_sent || clarification.final_response) {
        return "view";
      }
      return "edit-full";
    }
    if (!isClientView && !clarification?.is_draft_response) {
      return "view";
    }
    if (clarification.status === ClarificationStatus.closed) {
      return "view";
    }
    return "respond";
  };

  const mode = getMode();

  const handleModalClosure = () => {
    if (!isSafeToClose()) {
      return;
    }

    onClose(uploadFiles);
  }

  useEffect(() => {
    if (clarification) {
      setFormData({
        clarification_text: clarification.clarification_text || "",
        clarification_type_id: clarification.clarification_type_id || 0,
        relates_to: clarification.relates_to || "",
        owner: clarification.owner || "",
        clarification_impact_id: clarification.clarification_impact_id || null,
        status: clarification.status || null,
        final_response: clarification.final_response || "",
        files: clarification.files || [],
      });
    } else {
      setFormData({
        clarification_text: "",
        clarification_type_id: clarificationTypes[0]?.id || 0,
        relates_to: "",
        owner: "",
        clarification_impact_id: null,
        status: null,
        final_response: "",
        files: [],
      });
    }
    setErrors({});
  }, [clarification, clarificationTypes, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === "create" || mode === "edit-full") {
      if (!formData.clarification_text.trim()) {
        newErrors.clarification_text = "Clarification text is required";
      }
      if (!formData.clarification_type_id) {
        newErrors.clarification_type_id = "Type is required";
      }
      if (!formData.owner.trim()) {
        newErrors.owner = "Owner is required";
      }
    }

    if (mode === "respond" && !formData.final_response.trim()) {
      newErrors.final_response = "Response is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      let payload: CreateClarificationPayload | UpdateClarificationPayload;

      if (mode === "respond") {
        const responsePayload = {
          final_response: formData.final_response,
          clarification_impact_id: (formData.clarification_impact_id as ClarificationImpact) || undefined,
        } as SubmitClarificationResponsePayload;

        const result = await onSupplierSave(responsePayload, clarification?.id);
        if (result) {
          onClose();
        }

        return;
      }

      if (mode === "create") {
        payload = {
          clarification_text: formData.clarification_text,
          clarification_type_id: formData.clarification_type_id,
          relates_to: formData.relates_to || undefined,
          owner: formData.owner,
        };
      } else if (mode === "edit-full") {
        payload = {
          clarification_text: formData.clarification_text,
          clarification_type_id: formData.clarification_type_id,
          relates_to: formData.relates_to || undefined,
          owner: formData.owner,
        };
      } else {
        payload = {
          status: (formData.status as ClarificationStatus) || undefined,
        };
      }

      const success = await onClientSave(payload, clarification?.id);

      if (success) {
        onClose();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | FileableFile[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    if (field === "files") {
      setUploadFiles(value as FileableFile[]);
    }
  };

  const getModalHeader = () => {
    const headers: Record<ModalMode, { bgColor: string; description: string; icon: ReactNode; iconColor: string; title: string; }> = {
      create: {
        icon: <FiPlusCircle className="stroke-blue-400" size={40} />,
        bgColor: "bg-brand-secondary",
        iconColor: "text-utility-brand-700",
        title: `Create Clarification for ${supplierName}`,
        description: "Request additional information from the supplier.",
      },
      view: {
        icon: <FiFileText className="stroke-blue-400" size={40} />,
        bgColor: "bg-gray-100",
        iconColor: "text-gray-700",
        title: `Clarification for ${clarification?.supplier_name}`,
        description: "Review the clarification details and response.",
      },
      respond: {
        icon: <FiMessageCircle className="stroke-blue-400" size={40} />,
        bgColor: "bg-brand-secondary",
        iconColor: "text-utility-brand-700",
        title: "Respond to Clarification",
        description: "Draft your response to this clarification request.",
      },
      edit: {
        icon: <FiEdit2 className="stroke-blue-400" size={30} />,
        bgColor: "bg-brand-secondary",
        iconColor: "text-utility-brand-700",
        title: `Update Clarification for ${clarification?.supplier_name}`,
        description: "Update the status or impact of this clarification.",
      },
      "edit-full": {
        icon: <FiEdit2 className="stroke-blue-400" size={30} />,
        bgColor: "bg-brand-secondary",
        iconColor: "text-utility-brand-700",
        title: `Edit Clarification for ${clarification?.supplier_name}`,
        description: "Edit the clarification details before a response is received.",
      },
    };
    return headers[mode];
  };

  // view mode
  const isViewOnly = mode === "view";

  const FooterComponent = () => {
    if (isViewOnly) {
      return (
        <div className="flex w-full justify-end">
          <Button onClick={handleModalClosure}>
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="flex w-full justify-between gap-2">
        <Button onClick={handleModalClosure} btntype="base" className="!w-full" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          className="flex !w-full gap-3"
          onClick={handleSubmit}
          disabled={isLoading}
          data-testid="clarification-submit-button"
        >
          {isLoading ? (
            <Spinner size="sm" />
          ) : mode === "create" ? (
            "Create"
          ) : mode === "edit-full" ? (
            "Save Changes"
          ) : mode === "respond" ? (
            "Save Draft"
          ) : (
            "Save"
          )}
        </Button>
      </div>
    );
  };

  const impactOptions = Object.entries(CLARIFICATION_IMPACT_LABELS).map(
    ([value, label]) => ({
      value: Number(value) as ClarificationImpact,
      label,
    })
  );

  const header = getModalHeader();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClosure}
      title={header.title}
      description={header.description}
      customIcon={header.icon}
      size="xl"
      customFooter={<FooterComponent />}
    >
      <div className="space-y-4">
        {clarification && <Header clarification={clarification} mode={mode} />}

        {(mode === "create" || mode === "edit-full") && (
          <ClientCreation clarificationTypes={clarificationTypes} errors={errors} formData={formData} handleInputChange={handleInputChange} impactOptions={impactOptions} />
        )}

        {(mode === "view" || mode === "respond" || mode === "edit") && (
          <>
            <div className="rounded-lg bg-gray-50 p-4 gap-6 flex flex-col">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Clarification Request
                </label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {clarification?.clarification_text}
                </p>
              </div>
              {clarification?.relates_to && <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Relates To
                </label>
                <p className="text-sm text-gray-900">{clarification?.relates_to || "-"}</p>
              </div>}
            </div>
          </>
        )}

        {mode === "respond" && <ClarificationSupplierResponse clarificationId={clarification.id} errors={errors} formData={formData} handleInputChange={handleInputChange} initialFiles={clarification.files} isViewOnly={isViewOnly} />}

        {mode === "view" && clarification?.final_response && <ViewSupplierResponse clarification={clarification} />}

        {mode === "edit" && (
          <>
            {clarification?.final_response && (
              <div className="rounded-lg bg-blue-50 p-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Supplier Response
                </label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {clarification.final_response}
                </p>
              </div>
            )}

            {/* @ts-ignore */}
            <Input
              label="Impact"
              name="impact"
              type="select"
              options={impactOptions}
              value={formData.clarification_impact_id}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleInputChange("impact", e.target.value)
              }
            />
          </>
        )}
      </div>
    </Modal>
  );
};
